export interface LatLng {
  lat: number
  lng: number
}

// WGS84 ellipsoid constants
const a = 6378137.0
const f = 1 / 298.257223563
const e2 = 2 * f - f * f
const ep2 = e2 / (1 - e2)
const k0 = 0.9996
const E0 = 500000

function utmToLatLng(zone: number, band: string, easting: number, northing: number): LatLng | null {
  const isSouthern = band.toUpperCase() < 'N'
  const N0 = isSouthern ? 10000000 : 0

  const x = easting - E0
  const y = northing - N0

  const M = y / k0
  const mu = M / (a * (1 - e2 / 4 - (3 * e2 * e2) / 64 - (5 * e2 * e2 * e2) / 256))

  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2))

  const phi1 =
    mu +
    ((3 * e1) / 2 - (27 * e1 * e1 * e1) / 32) * Math.sin(2 * mu) +
    ((21 * e1 * e1) / 16 - (55 * e1 * e1 * e1 * e1) / 32) * Math.sin(4 * mu) +
    ((151 * e1 * e1 * e1) / 96) * Math.sin(6 * mu) +
    ((1097 * e1 * e1 * e1 * e1) / 512) * Math.sin(8 * mu)

  const sinPhi1 = Math.sin(phi1)
  const cosPhi1 = Math.cos(phi1)
  const tanPhi1 = Math.tan(phi1)

  const N1 = a / Math.sqrt(1 - e2 * sinPhi1 * sinPhi1)
  const T1 = tanPhi1 * tanPhi1
  const C1 = ep2 * cosPhi1 * cosPhi1
  const R1 = (a * (1 - e2)) / Math.pow(1 - e2 * sinPhi1 * sinPhi1, 1.5)
  const D = x / (N1 * k0)

  const latRad =
    phi1 -
    ((N1 * tanPhi1) / R1) *
      (D * D / 2 -
        ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * ep2) * D * D * D * D) / 24 +
        ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * ep2 - 3 * C1 * C1) *
          D * D * D * D * D * D) / 720)

  const lngRad =
    (D -
      ((1 + 2 * T1 + C1) * D * D * D) / 6 +
      ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * ep2 + 24 * T1 * T1) *
        D * D * D * D * D) / 120) /
    cosPhi1

  const lat = (latRad * 180) / Math.PI
  const lng = (lngRad * 180) / Math.PI + (zone - 1) * 6 - 180 + 3

  if (!isFinite(lat) || !isFinite(lng)) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null

  return { lat, lng }
}

function parseDecimalDegrees(input: string): LatLng | null {
  const match = input.match(
    /^([+-]?\d{1,3}(?:\.\d+)?)\s*°?\s*[,\s]\s*([+-]?\d{1,3}(?:\.\d+)?)\s*°?$/,
  )
  if (!match) return null

  const lat = parseFloat(match[1]!)
  const lng = parseFloat(match[2]!)

  if (!isFinite(lat) || !isFinite(lng)) return null
  if (lat < -90 || lat > 90) return null
  if (lng < -180 || lng > 180) return null

  return { lat, lng }
}

function parseUTM(input: string): LatLng | null {
  // Matches: "10S 551449 4182058" or "10S 551449E 4182058N"
  const match = input.match(/^(\d{1,2})([C-HJ-NP-X])\s+(\d+)E?\s+(\d+)N?$/i)
  if (!match) return null

  const zone = parseInt(match[1]!, 10)
  const band = match[2]!
  const easting = parseInt(match[3]!, 10)
  const northing = parseInt(match[4]!, 10)

  if (zone < 1 || zone > 60) return null
  // I and O are not valid UTM band letters
  const upperBand = band.toUpperCase()
  if (upperBand === 'I' || upperBand === 'O') return null

  return utmToLatLng(zone, band, easting, northing)
}

export function parseCoordinates(input: string): LatLng | null {
  const trimmed = input.trim()
  if (!trimmed) return null

  return parseDecimalDegrees(trimmed) ?? parseUTM(trimmed)
}

export function formatCoordinates(lat: number, lng: number): string {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`
}
